import * as React from 'react';

import {
  usePushConfiguration,
  useRegisterPushSubscription,
  type PushSubscriptionPayload,
} from './api';
import { isRunningInstalled } from './install-prompt';
import {
  isAppleTouchPlatform,
  pushBlockedByIosInstall,
  pushCanBeGranted,
} from './supported-platforms';

/**
 * Web push registration, and the one rule it is built around: **the browser's permission
 * dialogue is never triggered on load** (`MEM-040` AC-2, AC-3).
 *
 * ## Why that rule needs a module rather than a convention
 *
 * `Notification.requestPermission()` is one call, it is available from anywhere, and calling it
 * during a render or an effect is the most natural-looking thing in the world. It is also
 * irreversible: a member who answers Block in their first two seconds has answered permanently,
 * the browser hides the control that would let them change their mind, and there is no second
 * chance for the life of that origin. Most people answer Block to a dialogue they did not ask
 * for.
 *
 * So: `requestPermission` is called from exactly one function in this repository —
 * {@link useMemberPushRegistration}'s `enable` — that function is `async` and takes no arguments
 * because it is a click handler, and no effect anywhere calls it.
 * `__tests__/member/pwa/push-is-never-asked-for-on-load.test.ts` mounts the app's surfaces with a
 * spy on `Notification.requestPermission` and requires zero calls.
 *
 * ## What decides whether the offer is even drawn
 *
 * {@link decidePushOffer}, which is a pure function so the decision can be asserted without a
 * browser. Five inputs and one of them is the whole point — `hasReason`. The offer is not drawn
 * until the member is looking at something push would have told them about. `MEM-040`'s Notes
 * name the moment: their first match release.
 *
 * ## Declining costs nothing (`MEM-040` AC-5)
 *
 * Every branch below ends in the app behaving identically. A member on a browser with no push, a
 * deployment with no VAPID key configured, a member who dismissed the offer, and a member who
 * pressed Block in the browser dialogue all reach the same place: no notice, and every badge,
 * screen and count exactly as it was. Nothing in this module is read by the shell, the badge or
 * any screen — see `notification-devices.ts`, which states that as the invariant it is.
 */

/** Where a dismissal is remembered. A UI preference, not a queued write — see the header. */
const PUSH_OFFER_DISMISSED_KEY = 'h2hspark.push-offer-dismissed';

export type PushPermission = 'default' | 'granted' | 'denied';

export interface PushOfferInput {
  /** The browser has a service worker, a `PushManager` and a `Notification` API. */
  readonly supported: boolean;
  /** This deployment has an application server key. */
  readonly configured: boolean;
  readonly permission: PushPermission;
  /** The member has already said no to the in-app offer. */
  readonly dismissed: boolean;
  /**
   * The member is looking at something push would have told them about.
   *
   * **This is the criterion.** False means no offer, which means no dialogue, which is the state
   * every member is in on their first load of every session until they have a reason.
   */
  readonly hasReason: boolean;
}

/**
 * Whether to draw the in-app push offer.
 *
 * Returns `false` for every reason there is not to ask, and the reasons are not ranked — an
 * unsupported browser, an unconfigured deployment, a member who already said no in the app, a
 * member who already answered the browser dialogue either way, and a member with no reason yet
 * all produce the same silence. Ranking them would invite a "well, in *this* case" exception,
 * and the exception would be the one that fires on load.
 */
export function decidePushOffer(input: PushOfferInput): boolean {
  if (!input.supported) return false;
  if (!input.configured) return false;
  if (input.permission !== 'default') return false;
  if (input.dismissed) return false;
  return input.hasReason;
}

/**
 * Whether this browser can do web push at all.
 *
 * Capabilities first, and then the one platform fact capabilities cannot express: Safari on iOS
 * exposes `PushManager` in a tab and refuses to grant permission there — push arrives only once
 * the app is on the home screen (`PLT-008` AC-6). Offering it in an iOS tab would be raising a
 * dialogue that cannot say yes, which is the exact spend of the one chance `MEM-040` AC-3 is
 * about. See `supported-platforms.ts`.
 */
export function isPushSupported(): boolean {
  return pushCanBeGranted({
    supported: isPushCapable(),
    appleTouch: isAppleTouchPlatform(navigator),
    alreadyInstalled: isRunningInstalled(),
  });
}

/**
 * Whether the browser has the push APIs, **before** the iOS platform rule is applied.
 *
 * Separated from {@link isPushSupported} for `PLT-008` AC-6. The two answers differ in exactly one
 * situation — Safari in an iOS tab, which exposes `PushManager` and will not grant permission
 * through it — and that situation is the one the member has to be told about. Folding them
 * together, as they were, meant the app could not distinguish "this browser cannot do push" from
 * "this browser cannot do push *yet*", and so said nothing in both cases.
 */
export function isPushCapable(): boolean {
  if (typeof window === 'undefined') return false;
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/**
 * Whether push is withheld only because the app is not on the home screen (`PLT-008` AC-6).
 *
 * The condition `PushNotice` states the consequence on. See `supported-platforms.ts` for why this
 * one reason is worth naming when the others are not.
 */
export function isPushWaitingOnIosInstall(): boolean {
  if (typeof window === 'undefined') return false;
  return pushBlockedByIosInstall({
    capable: isPushCapable(),
    appleTouch: isAppleTouchPlatform(navigator),
    alreadyInstalled: isRunningInstalled(),
  });
}

/** The browser's current answer, or `default` where there is no `Notification` to ask. */
export function readPushPermission(): PushPermission {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'default';
  const permission = window.Notification.permission;
  if (permission === 'granted' || permission === 'denied') return permission;
  return 'default';
}

/**
 * Decodes the base64url VAPID public key the API sends into the `Uint8Array`
 * `pushManager.subscribe` wants.
 *
 * Exported because it is the one piece of this file with an off-by-one in it — base64url swaps
 * two characters and drops the padding, and a key decoded a byte short fails inside the browser
 * with an opaque `InvalidAccessError` that reads like a permissions problem.
 */
export function decodeApplicationServerKey(base64Url: string): Uint8Array<ArrayBuffer> {
  const padded = base64Url.padEnd(Math.ceil(base64Url.length / 4) * 4, '=');
  const base64 = padded.replace(/-/gu, '+').replace(/_/gu, '/');
  const binary = atob(base64);
  // Backed by an explicit `ArrayBuffer` rather than the default: `subscribe` wants a
  // `BufferSource`, and TypeScript's `Uint8Array` is generic over a buffer that may be shared —
  // which a `SharedArrayBuffer` cannot be, so the unannotated version does not assign.
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

/** Reads the remembered dismissal. Private browsing throws on `localStorage`; treat it as "no". */
function readDismissed(): boolean {
  try {
    return window.localStorage.getItem(PUSH_OFFER_DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
}

export interface PushRegistration {
  /** Draw the offer. Already the whole of {@link decidePushOffer}; callers need no other check. */
  readonly shouldOffer: boolean;
  /**
   * State the iOS consequence instead (`PLT-008` AC-6).
   *
   * Never true at the same time as {@link shouldOffer} — on iOS in a tab the offer is withheld,
   * and this is what stands in its place. The caller draws an explanation, not a control: there
   * is nothing for the app to do here, and the member's action is in the Share menu.
   */
  readonly iosNeedsInstall: boolean;
  /**
   * Asks the browser for permission and, if granted, subscribes and registers.
   *
   * **Only ever called from a member's own tap.** Resolves to what happened, so the caller can
   * say so; it never throws, because a failed registration is not something to interrupt a member
   * about — they lose push and nothing else.
   */
  readonly enable: () => Promise<'granted' | 'denied' | 'failed'>;
  /** Remembers a no and takes the offer down. Nothing else in the app changes. */
  readonly dismiss: () => void;
}

/**
 * The registration path, wired to the API.
 *
 * `hasReason` is the caller's to supply and is the only argument, because it is the only part of
 * the decision that belongs to a screen — everything else is a fact about the browser, the
 * deployment or the member's earlier answers.
 */
export function useMemberPushRegistration(parameters: {
  readonly hasReason: boolean;
  readonly enabled: boolean;
}): PushRegistration {
  const supported = isPushSupported();
  // `PLT-008` AC-6: the consequence is only worth stating where push actually exists to be lost,
  // so the iOS case asks the deployment the same question the offer does. A build with no
  // application server key has no push on any platform, and telling an iPhone member to install
  // for notifications that nobody sends would be worse than the silence it replaces.
  const waitingOnIosInstall = isPushWaitingOnIosInstall();
  const configuration = usePushConfiguration({
    enabled: parameters.enabled && (supported || waitingOnIosInstall),
  });
  const register = useRegisterPushSubscription();

  const [dismissed, setDismissed] = React.useState(false);
  React.useEffect(() => {
    setDismissed(readDismissed());
  }, []);

  // Read once per render rather than held in state: the browser can change this from its own UI
  // — the padlock menu — and there is no event to subscribe to. Reading it is free.
  const permission = readPushPermission();

  const shouldOffer = decidePushOffer({
    supported,
    configured: configuration.data?.configured === true,
    permission,
    dismissed,
    hasReason: parameters.hasReason,
  });

  const publicKey = configuration.data?.publicKey ?? null;

  const enable = React.useCallback(async (): Promise<'granted' | 'denied' | 'failed'> => {
    if (publicKey === null) return 'failed';
    try {
      // The dialogue. One call, in a click handler, and nowhere else — see the header.
      const outcome = await window.Notification.requestPermission();
      if (outcome !== 'granted') return 'denied';

      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.subscribe({
        // Every push must carry a payload the member sees. The alternative — silent pushes — is
        // what browsers revoke the permission for.
        userVisibleOnly: true,
        applicationServerKey: decodeApplicationServerKey(publicKey),
      });
      await register.mutateAsync(subscription.toJSON() as PushSubscriptionPayload);
      return 'granted';
    } catch {
      // A failed subscribe or a failed register. The member keeps every in-app surface
      // (`MEM-040` AC-5); what they do not get is push.
      return 'failed';
    }
  }, [publicKey, register]);

  const dismiss = React.useCallback((): void => {
    setDismissed(true);
    try {
      window.localStorage.setItem(PUSH_OFFER_DISMISSED_KEY, 'true');
    } catch {
      // Private browsing. The offer is down for this session, which is the part that matters.
    }
  }, []);

  return {
    shouldOffer,
    iosNeedsInstall:
      waitingOnIosInstall && configuration.data?.configured === true && parameters.hasReason,
    enable,
    dismiss,
  };
}
